"use client";

import Link from "next/link";
import React from "react";

import AnimatedTags from "./AnimatedTags";
import BookAppointmentButton from "../utilities/BookAppointmentButton";

export interface AnimatedHeroSectionSanityProps {
  animatedTags?: string[];
  animationInterval?: number;
  title?: string;
  subtitle?: string;
  buttonLabel?: string;
  buttonLink?: string;
  buttonBgColor?: string;
  buttonTextColor?: string;
  buttonHoverColor?: string;
  usePastelColors?: boolean;
}

/**
 * Animated Hero Section - Sanity CMS Version
 * Displays rotating animated tags with static title, subtitle, and CTA button
 */
export default function AnimatedHeroSectionSanity({
  animatedTags = [
    "Hair Loss Assessment",
    "Hair Transplant",
    "Non-Surgical Treatments",
  ],
  animationInterval = 2500,
  title = "personalized to you",
  subtitle = "Customized care starts here",
  buttonLabel = "Book an Appointment",
  buttonLink = "/appointment",
  buttonBgColor = "bg-violet-950",
  buttonTextColor = "text-white",
  buttonHoverColor = "hover:bg-violet-800",
  usePastelColors = true,
}: AnimatedHeroSectionSanityProps) {
  return (
    <section className="pt-4 pb-6 md:pt-6 md:pb-8">
      <div className="mx-auto max-w-7xl px-6">
        <div className="flex flex-col items-start text-left">
          {/* Animated Tags */}
          <div className="mb-0">
            <h1 className="font-exo2 whitespace-nowrap text-4xl font-bold leading-tight text-gray-900 md:text-6xl lg:text-7xl">
              <AnimatedTags
                tags={animatedTags}
                interval={animationInterval}
                usePastelColors={usePastelColors}
                className="inline"
              />
            </h1>
          </div>

          {/* Title text - same font size as animated tags */}
          {title && (
            <p
              className="-mt-2 mb-4 text-4xl font-bold leading-tight text-black md:text-6xl lg:text-7xl"
              style={{ letterSpacing: "-0.0968125rem" }}
            >
              {title}
            </p>
          )}

          {/* Subtitle */}
          {subtitle && (
            <p className="mb-8 text-lg text-black md:text-xl">{subtitle}</p>
          )}

          {/* CTA Button */}
          {buttonLabel && buttonLink && (
            <Link
              href={buttonLink}
              className="inline-block rounded-full bg-black px-4 py-2 text-sm font-medium transition-colors hover:bg-gray-800 md:text-base"
              style={{ color: "#F7E9AE" }}
            >
              {buttonLabel}
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}
